import { defineStore } from "pinia";
import { ref } from "vue";
import { AxiosError, AxiosResponse, AxiosStatic } from "axios";
import { app } from "@/main";
import { IUtenti } from "@/interfaces/response/utenti/IUtenti";
import { authApi } from "@/requests/auth";
import { ITitolari } from "@/interfaces/response/utenti/ITitolari";
import { IDatiTitolariCompact } from "@/interfaces/response/utenti/IDatiTitolariCompact";
import { NotificationStatus } from "@/enums/components/general/NotificationStatus";
import { INotification } from "@/interfaces/components/general/INotification";
import { useNotificationStore } from "@/stores/notification";
/*import { IDeleghe } from "@/interfaces/response/utenti/IDeleghe";*/
import { EVENTS, sendEvent } from "@/emitter_mitt";
import { IStrutturePO } from "@/interfaces/models/IStrutturePO";
import ssoUtils from "@/helper/ssoUtils";
import { Lock } from "@/helper/lock";
import { useServiceDetailStore } from "@/stores/service/serviceDetail";
import enMessages from "@/locales/en.json";
import itMessages from "@/locales/it.json";
import { loginApi } from '@/requests/login';
import { UserRole } from "@/enums/utenti/UserRole";
import router from "@/router";

export const useUserStore = defineStore("user", () => {
  const notificationStore = useNotificationStore();
  const user = ref<IUser>();
  const titolari = ref<ITitolari[] | undefined>(undefined);
  const titolareUtente = ref<ITitolari | undefined>(undefined);
  const axios: AxiosStatic = app.config.globalProperties.$axios;
  const lock = new Lock();
  const serviceDetailStore = useServiceDetailStore();

  async function isAuthenticated(): Promise<boolean> {
    try {
      await lock.acquire();
      if (user.value !== undefined) {
        return true;
      }

      const isUserLogged = await isLoggedUser();
      if (!isUserLogged) {
        clearUser(); // Clear user data and cookies if invalid
        return false;
      }

      return true;
    } finally {
      lock.release();
    }
  }


  async function getUser(): Promise<IUser | undefined> {
    if (user.value !== undefined) {
      return user.value;
    }
    return await isAuthenticated().then(() => user.value);
  }

  async function getTitolari(filter?: IFilter): Promise<ITitolari[]> {
    return await axios.get<ITitolari[]>(authApi.GET_TITOLARI(filter?.active, filter?.filterText))
      .then((titolariUtente: AxiosResponse<ITitolari[]>) => {
        titolari.value = titolariUtente.data;
        return titolariUtente.data;
      }).catch((error) => {
        console.error(error);
        titolari.value = undefined;
        return [];
      });
  }

  /*async function getDeleghe(titolareId: string): Promise<IDeleghe[]> {
    return await axios.get<IDeleghe[]>(authApi.GET_DELEGHE_TITOLARE(titolareId))
      .then((deleghe: AxiosResponse<IDeleghe[]>) => {
        return deleghe.data;
      }).catch((error) => {
        console.error(error);
        return [];
      });
  }*/

  async function getUserTitolare(): Promise<ITitolari | undefined> {
    if (titolareUtente.value !== undefined) {
      return titolareUtente.value;
    }
    return getUser().then((user: IUser | undefined) => {
      if (user !== undefined) {
        return axios.get<ITitolari>(authApi.GET_TITOLARE_UTENTE(user.cf, user.cf)).then((titolariUtente: AxiosResponse<ITitolari>) => {
          titolareUtente.value = titolariUtente.data;
          return Promise.resolve(titolariUtente.data);
        }).catch((error: any) => {
          console.error(error);
          titolareUtente.value = undefined;
          return Promise.resolve(undefined);
        });
      }
    });
  }

  async function getPersonaGiuridica(code: string): Promise<ITitolari | undefined> {
    const language = serviceDetailStore.language || 'en';
    const messages = language === 'it' ? itMessages : enMessages;
    const { data } = await axios.get<ITitolari>(authApi.GET_PERSONA_GIURIDICA(code)).catch((error) => {
      console.error(error);
      let message = "Partita IVA o C.F. non trovato. Inserire i dati manualmente.";
      if (error.response.status == 400 && error.response.data.message)
        message = error.response.data.message;
      notificationStore.addNotification(
        {
          title: messages.getPersonaGiuridicaTitle,
          status: NotificationStatus.ERROR,
          message
        } as INotification);
      return error.response;
    });

    return data as ITitolari | undefined;

  }

  function updateUser(emailAlt: string | null, phone: string | null, provincia: string | null): Promise<any> {
    const language = serviceDetailStore.language || 'en';
    const messages = language === 'it' ? itMessages : enMessages;
    const [url, data] = authApi.UPDATE(emailAlt, phone, provincia);
    return new Promise((resolve, reject) => axios.post<IUtenti>(url, data)
      .then((el: AxiosResponse<IUtenti>) => {
        const utente = el.data;
        user.value = {
          id: utente.utentiId,
          firstname: utente.nome,
          lastname: utente.cognome,
          email: utente.email,
          emailAlt: utente.emailAlt,
          phone: utente.telefono,
          cf: utente.codiceFiscale,
          sesso: utente.sesso,
          provincia: utente.provincia,
          luogoNascita: utente.luogoNascita,
          dataNascita: utente.dataNascita,
          appio: utente.appio
        } as IUser;
        notificationStore.addNotification(
          {
            title: messages.updateUserSuccesMessage,
            status: NotificationStatus.SUCCESS
          } as INotification);
        resolve(el);
      })
      .catch((error) => {
        console.error(error);
        notificationStore.addNotification(
          {
            title: messages.updateUserErrorMessage,
            status: NotificationStatus.ERROR
          } as INotification);
        reject(error);
      }));
  }

  function updateUserChannels(email: boolean, appio: boolean): Promise<any> {
    const language = serviceDetailStore.language || 'en';
    const messages = language === 'it' ? itMessages : enMessages;
    const [url, data] = authApi.UPDATE_USER_CHANNELS(email, appio);
    return new Promise((resolve, reject) => axios.post<IUtenti>(url, data)
      .then((el: AxiosResponse<IUtenti>) => {
        notificationStore.addNotification(
          {
            title: messages.updateChannelSuccesMessage,
            status: NotificationStatus.SUCCESS
          } as INotification);
        resolve(el);
      })
      .catch((error) => {
        console.error(error);
        notificationStore.addNotification(
          {
            title: messages.updateChannelErrorMessage,
            status: NotificationStatus.ERROR
          } as INotification);
        reject(error);
      }));
  }

  function updateTitolare(formData: FormData): Promise<any> {
    const language = serviceDetailStore.language || 'en';
    const messages = language === 'it' ? itMessages : enMessages;
    return new Promise((resolve, reject) => axios.post(authApi.UPDATE_TITOLALRE(), formData, {
      headers: {
        "Content-Type": "multipart/form-data"
      },
      onUploadProgress: (event: ProgressEvent) => {
        Math.round((100 * event.loaded) / event.total);
      }
    })
      .then((el: AxiosResponse<any>) => {
        // const titolareId = el.data;
        notificationStore.addNotification(
          {
            title: messages.updateUserSuccesMessage,
            status: NotificationStatus.SUCCESS
          } as INotification);
        resolve(el);
      })
      .catch((error: AxiosError) => {
        console.log(error);
        console.error(error);
        const msg = error.response?.status === 422 ? error.response.data :messages.genericNotificationErrorMessage;
        notificationStore.addNotification(
          {
            title: msg,
            status: NotificationStatus.ERROR
          } as INotification);
        reject(error);
      }));
  }

  async function getDatiTitolari(titolareId: string | number, tipo: string): Promise<IDatiTitolariCompact> {
    return axios.get<IDatiTitolariCompact>(authApi.GET_DATI_TITOLARE(titolareId, tipo))
      .then((response: AxiosResponse<IDatiTitolariCompact>) => {
        return response.data;
      }).catch(() => {
        return { dati: {} } as IDatiTitolariCompact;
      });
  }

  async function tryGetJwt() : Promise<any> {
    const jwt = await axios.get<string>(authApi.GET_JWT());

    if( jwt && jwt.data)
      document.cookie = `jwt=${jwt.data}; path=/; secure`;
  }

  async function isLoggedUser(): Promise<boolean> {
    const language = serviceDetailStore.language || 'en';
    const messages = language === 'it' ? itMessages : enMessages;
    try {
      let jwt = ssoUtils.getCookie("jwt");

      // Check if the JWT cookie exists
      if (!jwt) {

        jwt=await tryGetJwt();
        // notificationStore.addNotification(
        //     {
        //       title: messages.jwtNotFound,
        //       status: NotificationStatus.ERROR
        //     } as INotification);
        user.value = undefined;
        //return false;
      }


      const userDataResponse = await axios.get<IUtenti>(authApi.GET_CURRENT_USER());
      const utente = userDataResponse.data;
      const role = utente.role || UserRole.USER
      localStorage.setItem('userRole', role);
      user.value = {
        id: utente.utentiId,
        firstname: utente.nome,
        lastname: utente.cognome,
        email: utente.email,
        emailAlt: utente.emailAlt,
        cf: utente.codiceFiscale,
        sesso: utente.sesso,
        phone: utente.telefono,
        provincia: utente.provincia,
        luogoNascita: utente.luogoNascita,
        dataNascita: utente.dataNascita,
        appio: utente.appio,
      } as IUser;

      return true;
    } catch (error) {
      /*notificationStore.addNotification(
          {
            title: messages.jwtValidationError,
            status: NotificationStatus.ERROR
          } as INotification);*/
      console.warn(messages.jwtValidationError)
      clearUser();
      return false;
    }
  }

  function revocaDelega(idDelegaUT: number) {
    const language = serviceDetailStore.language || 'en';
    const messages = language === 'it' ? itMessages : enMessages;
    return new Promise((resolve, reject) => axios.put(authApi.REVOCA_DELEGA(idDelegaUT)).then((r) => {
      notificationStore.addNotification(
        {
          title: messages.updateUserSuccesMessage,
          status: NotificationStatus.SUCCESS
        } as INotification);
      resolve(r);
    }).catch((error) => {
      console.error(error);
      notificationStore.addNotification(
        {
          title: messages.genericNotificationErrorMessage,
          status: NotificationStatus.ERROR
        } as INotification);
      reject(error);
    }));
  }

  async function forceLoginIfNeed() {
    if (!(await isAuthenticated())) {
      login();
    }
  }

  function clearUser() {
    user.value = undefined;
    ssoUtils.eraseCookie("jwt");
  }

 async function logout() {
  //  clearUser();
    // If JWT exists, proceed to fetch user data
   try {
     const response = await axios.post(authApi.POST_LOGOUT_URL(),"",{
       maxRedirects: 0,
       headers: {
       "Content-Type": "application/x-www-form-urlencoded"
     },
       validateStatus: (status: number) =>{
           return status >= 200 && status < 400;
     }
     });
   } catch (e) {
       console.warn(e)
     }
    user.value = undefined;
    ssoUtils.eraseCookie("jwt");
    login();
  }

  async function login() {
    // const { data } = await axios.get(authApi.GET_LOGIN_URL());
    // location.href = data;
    router.push({ path: "/login" });
  }

  async function getStruttureUtente(): Promise<IStrutturePO[]> {
    const language = serviceDetailStore.language || 'en';
    const messages = language === 'it' ? itMessages : enMessages;
    return new Promise<IStrutturePO[]>((resolve, reject) => axios.get(authApi.GET_STRUTTURE_UTENTE())
      .then((el: AxiosResponse<any>) => {
        resolve(el.data);
      })
      .catch((error: AxiosError) => {
        console.log(error);
        console.error(error);
        notificationStore.addNotification(
          {
            title: messages.genericNotificationErrorMessage,
            status: NotificationStatus.ERROR
          } as INotification);
        reject(error);
      }));
  }

  return {
    getTitolari,
    logout,
    isAuthenticated,
    getDatiTitolari,
    updateUser,
    updateUserChannels,
    getUser,
    forceLoginIfNeed,
    login,
    updateTitolare,
    getUserTitolare,
    getPersonaGiuridica,
    clearUser,
    revocaDelega,
    /*getDeleghe,*/
    getStruttureUtente
  };
});
